import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  LogOut, ArrowLeft, TrendingUp, TrendingDown,
  Euro, CalendarDays, FileDown, Plus, Trash2,
  Search, BarChart3, FileText, Coins, PiggyBank,
  Receipt, RefreshCw, ExternalLink, AlertCircle,
  Bell, ShieldAlert, CheckCircle2, Sparkles, Mail,
  Landmark, TrendingDown as TrendDown, PercentSquare,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#2C3E30', '#8A9A5B', '#C4A962', '#7a8a4d', '#3a5040', '#b5945a'];

const FORMULES = {
  'appartement-1': 'Apt Terrasse',
  'appartement-2': 'Apt Panorama',
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
  // Legacy
  'domaine-complet': 'Domaine + Salle',
  'gite-complet': 'Gite complet',
  'appartement-individuel': 'Appartement',
};

const PAYMENT_STATUS = {
  unpaid: { label: 'Non paye', color: 'bg-red-100 text-red-700 border-red-200' },
  deposit_paid: { label: 'Acompte recu', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paye', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const MONTHS_FR = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

function formatEuro(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(n || 0);
}

function useAdminApi() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) => {
    return axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } });
  }, [token]);
  const logout = useCallback(() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_email'); navigate('/admin'); }, [navigate]);
  useEffect(() => { if (!token) navigate('/admin'); }, [token, navigate]);
  return { api, logout };
}

function exportCSV(headers, rows, filename) {
  const bom = '\uFEFF';
  const csv = bom + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── KPI Card ────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${accent || 'bg-[#F5F5F0]'}`}>
          <Icon className="w-6 h-6 text-[#2C3E30]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#2C3E30]">{value}</p>
          <p className="text-xs text-[#595959] mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────
function LoanOverviewWidget({ api }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api('get', '/api/admin/loans/summary');
        setSummary((r.data || [])[0] || null);
      } catch (e) { /* silent */ }
    })();
  }, [api]);
  if (!summary) return null;
  const formatEuroLocal = (n) => n == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
  const next = summary.next_payment;
  return (
    <div className="bg-gradient-to-br from-[#2C3E30] to-[#3a5040] rounded-xl p-6 text-[#F2F0E9] shadow-[0_2px_8px_-2px_rgba(0,0,0,0.15)]" data-testid="loan-overview-widget">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#C4A962]">Emprunt bancaire</p>
          <h3 className="font-medium text-base mt-0.5">{summary.lender}</h3>
          <p className="text-xs text-[#F2F0E9]/60 mt-0.5">Fin prevue : {summary.end_date}</p>
        </div>
        <Landmark className="w-6 h-6 text-[#C4A962]" strokeWidth={1.5} />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase text-[#F2F0E9]/50 mb-0.5">Capital restant du</p>
          <p className="text-2xl font-light font-mono" data-testid="loan-outstanding">{formatEuroLocal(summary.outstanding_capital)}</p>
          <p className="text-[10px] text-[#F2F0E9]/60">sur {formatEuroLocal(summary.initial_amount)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-[#F2F0E9]/50 mb-0.5">Prochaine echeance</p>
          <p className="text-2xl font-light font-mono" data-testid="loan-next-amount">{formatEuroLocal(summary.next_payment_total)}</p>
          <p className="text-[10px] text-[#F2F0E9]/60">{next ? `${next.month} • ${summary.months_remaining} mois restants` : '—'}</p>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between text-[10px] text-[#F2F0E9]/70 mb-1">
          <span>Progression du remboursement</span>
          <span className="font-mono" data-testid="loan-progress-pct">{summary.progress_percent?.toFixed(1)} %</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#C4A962]" style={{ width: `${Math.min(100, summary.progress_percent || 0)}%` }} data-testid="loan-progress-bar" />
        </div>
      </div>
    </div>
  );
}

const LOAN_KIND_LABELS = { interest: 'Interets', capital: 'Capital', insurance: 'Assurance' };

function LoanTab({ api }) {
  const [loan, setLoan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [yearFilter, setYearFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ls, sm] = await Promise.all([
        api('get', '/api/admin/loans'),
        api('get', '/api/admin/loans/summary'),
      ]);
      setLoan((ls.data || [])[0] || null);
      setSummary((sm.data || [])[0] || null);
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur de chargement.');
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const generateCurrent = async () => {
    if (!loan) return;
    setBusy(true); setMessage('');
    try {
      const r = await api('post', `/api/admin/loans/${loan.id}/generate-expenses`);
      const created = (r.data.entries || []).filter(e => e.status === 'created').length;
      const skipped = (r.data.entries || []).filter(e => e.status !== 'created').length;
      setMessage(`Echeance ${r.data.month} : ${created} charge${created > 1 ? 's' : ''} creee${created > 1 ? 's' : ''}, ${skipped} deja existante${skipped > 1 ? 's' : ''}.`);
      await fetchAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de la generation.');
    } finally { setBusy(false); }
  };

  const generateAllPast = async () => {
    if (!loan) return;
    if (!window.confirm('Generer les charges pour tous les mois passes (y compris le mois en cours) ? Idempotent.')) return;
    setBusy(true); setMessage('');
    try {
      const r = await api('post', `/api/admin/loans/${loan.id}/generate-all-past`);
      setMessage(`${r.data.backfilled} mois traites : ${(r.data.months || []).join(', ')}`);
      await fetchAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors du backfill.');
    } finally { setBusy(false); }
  };

  const formatEuroLocal = (n) => n == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  if (loading) return <p className="text-sm text-[#8A8A85] p-8">Chargement du tableau d&apos;amortissement...</p>;
  if (!loan) return <p className="text-sm text-[#8A8A85] p-8">Aucun emprunt configure.</p>;

  const years = Array.from(new Set(loan.schedule.map(e => e.month.slice(0, 4)))).sort();
  const filteredSchedule = yearFilter === 'all' ? loan.schedule : loan.schedule.filter(e => e.month.startsWith(yearFilter));
  const todayYm = new Date().toISOString().slice(0, 7);

  return (
    <div className="space-y-6" data-testid="loan-tab">
      {message && <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200" data-testid="loan-message">{message}</div>}

      {/* Loan header + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="loan-header">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#C4A962]">Emprunt bancaire</p>
              <h2 className="font-semibold text-[#2C3E30] text-lg mt-0.5">{loan.reference}</h2>
              <p className="text-xs text-[#595959] mt-1">{loan.lender} • Taux {loan.rate_annual}% • {loan.differ_months} mois de differe • Fin {loan.end_date}</p>
            </div>
            <Landmark className="w-7 h-7 text-[#2C3E30]" strokeWidth={1.5} />
          </div>
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#F5F5F0]">
                <p className="text-[10px] uppercase text-[#8A8A85]">Montant initial</p>
                <p className="text-lg font-light text-[#2C3E30] font-mono" data-testid="loan-initial">{formatEuroLocal(summary.initial_amount)}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F5F0]">
                <p className="text-[10px] uppercase text-[#8A8A85]">Reste a rembourser</p>
                <p className="text-lg font-light text-[#B04A32] font-mono" data-testid="loan-remaining">{formatEuroLocal(summary.outstanding_capital)}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50">
                <p className="text-[10px] uppercase text-emerald-700">Capital rembourse</p>
                <p className="text-lg font-light text-emerald-800 font-mono" data-testid="loan-paid">{formatEuroLocal(summary.total_capital_paid)}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#C4A962]/10">
                <p className="text-[10px] uppercase text-[#8A6E2F]">Interets payes</p>
                <p className="text-lg font-light text-[#8A6E2F] font-mono" data-testid="loan-interest-paid">{formatEuroLocal(summary.total_interest_paid)}</p>
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] flex flex-col" data-testid="loan-next-panel">
          <p className="text-[10px] uppercase text-[#8A8A85] mb-1">Prochaine echeance</p>
          {summary?.next_payment ? (
            <>
              <p className="text-2xl font-light text-[#2C3E30] font-mono">{summary.next_payment.month}</p>
              <p className="text-3xl font-light text-[#C4A962] font-mono mt-2" data-testid="loan-next-total">{formatEuroLocal(summary.next_payment_total)}</p>
              <div className="text-xs text-[#595959] mt-3 space-y-0.5">
                <p>Capital : <span className="font-mono">{formatEuroLocal(summary.next_payment.capital)}</span></p>
                <p>Interets : <span className="font-mono">{formatEuroLocal(summary.next_payment.interest)}</span></p>
                <p>Assurance : <span className="font-mono">{formatEuroLocal(summary.next_payment.insurance)}</span></p>
              </div>
            </>
          ) : (
            <p className="text-sm text-[#595959]">Emprunt entierement rembourse.</p>
          )}
          <div className="mt-auto pt-3 flex flex-col gap-2">
            <Button data-testid="loan-generate-current-btn" onClick={generateCurrent} disabled={busy} size="sm" className="w-full rounded-full bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] text-xs">
              <Sparkles className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Generer les charges du mois
            </Button>
            <Button data-testid="loan-generate-all-btn" onClick={generateAllPast} disabled={busy} size="sm" variant="outline" className="w-full rounded-full text-xs border-[#E5E5E0]">
              <RefreshCw className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Backfill mois passes
            </Button>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {summary && (
        <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="loan-progress-panel">
          <div className="flex items-center justify-between text-xs text-[#595959] mb-2">
            <span>Progression du remboursement</span>
            <span className="font-mono text-[#2C3E30] font-medium" data-testid="loan-tab-progress-pct">{summary.progress_percent?.toFixed(1)} %</span>
          </div>
          <div className="h-3 rounded-full bg-[#F5F5F0] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#8A9A5B] to-[#C4A962] transition-all" style={{ width: `${Math.min(100, summary.progress_percent || 0)}%` }} data-testid="loan-tab-progress-bar" />
          </div>
          <p className="text-[10px] text-[#8A8A85] mt-2">{summary.months_remaining} mois restants sur {summary.months_total} • Aucune TVA sur les prêts bancaires (charges HT sans deduction TVA).</p>
        </div>
      )}

      {/* Amortization table */}
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="loan-schedule-table">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-[#2C3E30] text-lg flex items-center gap-2">
            <PercentSquare className="w-5 h-5 text-[#2C3E30]" strokeWidth={1.5} /> Tableau d&apos;amortissement
          </h3>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger data-testid="loan-year-filter" className="w-32 rounded-lg border-[#E5E5E0]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les annees</SelectItem>
              {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-[#F5F5F0]">
              <TableHead className="text-xs">Mois</TableHead>
              <TableHead className="text-xs text-right">Capital</TableHead>
              <TableHead className="text-xs text-right">Interets</TableHead>
              <TableHead className="text-xs text-right">Assurance</TableHead>
              <TableHead className="text-xs text-right">Total</TableHead>
              <TableHead className="text-xs text-center">Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredSchedule.map((e) => {
                const total = (e.capital || 0) + (e.interest || 0) + (e.insurance || 0);
                const isPast = e.month < todayYm || e.expense_generated;
                const isCurrent = e.month === todayYm;
                return (
                  <TableRow key={e.month} data-testid={`loan-schedule-row-${e.month}`} className={`${isCurrent ? 'bg-amber-50/40' : ''} hover:bg-[#FAFAF9]`}>
                    <TableCell className="text-xs font-mono">{e.month}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{e.capital > 0 ? formatEuroLocal(e.capital) : '—'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{formatEuroLocal(e.interest)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{formatEuroLocal(e.insurance)}</TableCell>
                    <TableCell className="text-xs text-right font-mono font-medium">{formatEuroLocal(total)}</TableCell>
                    <TableCell className="text-xs text-center">
                      {e.expense_generated ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Charges emises</Badge>
                      ) : isPast ? (
                        <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200 border text-[10px]">Passe</Badge>
                      ) : isCurrent ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px]">Ce mois</Badge>
                      ) : (
                        <Badge className="bg-sky-50 text-sky-700 border-sky-200 border text-[10px]">A venir</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────
function OverviewTab({ overview, api }) {
  if (!overview) return <div className="p-12 text-center text-[#595959]">Chargement...</div>;

  const typeData = [
    { name: 'Hebergements', value: overview.hebergement_revenue || 0 },
    { name: 'Salle des fetes', value: overview.salle_revenue || 0 },
  ].filter(d => d.value > 0);

  const monthData = (overview.by_month || []).map(m => ({
    name: m.month.length >= 7 ? MONTHS_FR[parseInt(m.month.split('-')[1])]?.substring(0, 3) + ' ' + m.month.split('-')[0].slice(2) : m.month,
    value: m.value,
  }));

  const accData = (overview.by_accommodation || []).filter(a => a.value > 0);

  return (
    <div className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Chiffre d'affaires total" value={formatEuro(overview.total_revenue)} icon={Coins} accent="bg-[#C4A962]/15" />
        <KpiCard label="Mois en cours" value={formatEuro(overview.month_revenue)} icon={CalendarDays} accent="bg-[#8A9A5B]/15" />
        <KpiCard label="Annee en cours" value={formatEuro(overview.year_revenue)} icon={TrendingUp} accent="bg-[#2C3E30]/10" />
        <KpiCard label="Taxe de sejour collectee" value={formatEuro(overview.total_tourist_tax)} icon={FileText} accent="bg-teal-50" />
        <KpiCard label="Ecritures manuelles" value={formatEuro(overview.entries_total)} icon={FileText} accent="bg-[#F5F5F0]" />
      </div>

      {/* Payment stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{overview.payment_stats?.paid || 0}</p>
          <p className="text-xs text-emerald-600">Payees</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{overview.payment_stats?.deposit_paid || 0}</p>
          <p className="text-xs text-amber-600">Acompte recu</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{overview.payment_stats?.unpaid || 0}</p>
          <p className="text-xs text-red-600">Non payees</p>
        </div>
      </div>

      {/* Loan widget */}
      <LoanOverviewWidget api={api} />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue by type */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h3 className="text-sm font-semibold text-[#2C3E30] mb-4">Hebergements vs Salle</h3>
          {typeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={typeData} cx="50%" cy="45%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                  {typeData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatEuro(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-[#595959] text-sm text-center py-12">Aucune donnee</p>}
        </div>

        {/* Revenue by month */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h3 className="text-sm font-semibold text-[#2C3E30] mb-4">Revenus par mois</h3>
          {monthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => formatEuro(v)} />
                <Bar dataKey="value" fill="#8A9A5B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-[#595959] text-sm text-center py-12">Aucune donnee</p>}
        </div>

        {/* Revenue by accommodation */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h3 className="text-sm font-semibold text-[#2C3E30] mb-4">Par hebergement</h3>
          {accData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={accData} cx="50%" cy="45%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                  {accData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatEuro(v)} />
                <Legend wrapperStyle={{ fontSize: 10, lineHeight: '18px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-[#595959] text-sm text-center py-12">Aucune donnee</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Monthly Tab ─────────────────────────────────────────
function MonthlyTab({ monthly }) {
  if (!monthly) return <div className="p-12 text-center text-[#595959]">Chargement...</div>;

  const handleExport = () => {
    const headers = ['Mois', 'Annee', 'Total Hebergements', 'Total Salle', 'Taxe de sejour', 'Total General', 'Nb reservations'];
    const rows = monthly.map(m => [MONTHS_FR[m.month], m.year, m.hebergement, m.salle, m.tourist_tax || 0, m.total, m.count]);
    exportCSV(headers, rows, `comptabilite_mensuelle_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const grandTotal = monthly.reduce((s, m) => s + m.total, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[#2C3E30]">Total : {formatEuro(grandTotal)}</h3>
        <Button data-testid="export-monthly-btn" onClick={handleExport} variant="outline" className="rounded-full text-sm border-[#E5E5E0]">
          <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> Exporter CSV
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        {monthly.length === 0 ? <div className="p-12 text-center text-[#595959]">Aucune donnee.</div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="text-[#2C3E30] font-semibold">Mois</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Annee</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Hebergements</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Salle des fetes</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Taxe sejour</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Reservations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthly.map(m => (
                  <TableRow key={m.month_key} className="hover:bg-[#FAFAF9]">
                    <TableCell className="font-medium">{MONTHS_FR[m.month]}</TableCell>
                    <TableCell>{m.year}</TableCell>
                    <TableCell className="text-right">{formatEuro(m.hebergement)}</TableCell>
                    <TableCell className="text-right">{formatEuro(m.salle)}</TableCell>
                    <TableCell className="text-right text-teal-700">{formatEuro(m.tourist_tax || 0)}</TableCell>
                    <TableCell className="text-right font-semibold text-[#2C3E30]">{formatEuro(m.total)}</TableCell>
                    <TableCell className="text-right">{m.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Yearly Tab ──────────────────────────────────────────
function YearlyTab({ yearly }) {
  if (!yearly) return <div className="p-12 text-center text-[#595959]">Chargement...</div>;

  const handleExport = () => {
    const headers = ['Annee', 'Hebergements', 'Salle des fetes', 'Taxe de sejour', 'Total', 'Evolution N-1'];
    const rows = yearly.map(y => [y.year, y.hebergement, y.salle, y.tourist_tax || 0, y.total, y.evolution !== null ? `${y.evolution}%` : '-']);
    exportCSV(headers, rows, `comptabilite_annuelle_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid="export-yearly-btn" onClick={handleExport} variant="outline" className="rounded-full text-sm border-[#E5E5E0]">
          <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> Exporter CSV
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        {yearly.length === 0 ? <div className="p-12 text-center text-[#595959]">Aucune donnee.</div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="text-[#2C3E30] font-semibold">Annee</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Hebergements</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Salle des fetes</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Taxe sejour</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Evolution N-1</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {yearly.map(y => (
                  <TableRow key={y.year} className="hover:bg-[#FAFAF9]">
                    <TableCell className="font-semibold">{y.year}</TableCell>
                    <TableCell className="text-right">{formatEuro(y.hebergement)}</TableCell>
                    <TableCell className="text-right">{formatEuro(y.salle)}</TableCell>
                    <TableCell className="text-right text-teal-700">{formatEuro(y.tourist_tax || 0)}</TableCell>
                    <TableCell className="text-right font-semibold text-[#2C3E30]">{formatEuro(y.total)}</TableCell>
                    <TableCell className="text-right">
                      {y.evolution !== null ? (
                        <span className={`inline-flex items-center gap-1 ${y.evolution >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {y.evolution >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {y.evolution >= 0 ? '+' : ''}{y.evolution}%
                        </span>
                      ) : <span className="text-[#595959]">-</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Details Tab ─────────────────────────────────────────
function DetailsTab({ api, onRefresh }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/admin/accounting/details?';
      if (yearFilter !== 'all') url += `year=${yearFilter}&`;
      if (monthFilter !== 'all') url += `month=${monthFilter}&`;
      if (typeFilter !== 'all') url += `acc_type=${typeFilter}&`;
      if (paymentFilter !== 'all') url += `payment_status=${paymentFilter}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      const res = await api('get', url);
      setDetails(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [api, yearFilter, monthFilter, typeFilter, paymentFilter, search]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const updatePayment = async () => {
    if (!editId) return;
    await api('put', `/api/admin/reservations/${editId}/payment`, editData);
    setEditId(null);
    setEditData({});
    fetchDetails();
    onRefresh();
  };

  const handleExport = () => {
    const headers = ['Date reservation', 'Client', 'Email', 'Type', 'Formule', 'Arrivee', 'Depart', 'Montant', 'Taxe sejour', 'Acompte (30%)', 'Solde', 'Statut paiement', 'Statut reservation'];
    const rows = details.map(r => [
      r.created_at?.slice(0, 10), r.name, r.email,
      r.category === 'salle' ? 'Salle' : 'Hebergement',
      r.accommodation_label, r.arrival_date, r.departure_date,
      r.amount, r.tourist_tax || 0, r.deposit_amount, r.balance,
      PAYMENT_STATUS[r.payment_status]?.label || r.payment_status, r.status,
    ]);
    exportCSV(headers, rows, `reservations_detail_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const years = [...new Set(details.map(r => r.arrival_date?.slice(0, 4)).filter(Boolean))].sort();

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
            <Input data-testid="accounting-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client..." className="pl-10 rounded-lg h-10" />
          </div>
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-year" className="w-[120px] rounded-lg h-10"><SelectValue placeholder="Annee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="filter-month" className="w-[130px] rounded-lg h-10"><SelectValue placeholder="Mois" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {MONTHS_FR.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="filter-type" className="w-[140px] rounded-lg h-10"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="hebergement">Hebergement</SelectItem>
            <SelectItem value="salle">Salle</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger data-testid="filter-payment" className="w-[150px] rounded-lg h-10"><SelectValue placeholder="Paiement" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="unpaid">Non paye</SelectItem>
            <SelectItem value="deposit_paid">Acompte recu</SelectItem>
            <SelectItem value="paid">Paye</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="export-details-btn" onClick={handleExport} variant="outline" className="rounded-full text-sm border-[#E5E5E0] h-10">
          <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> CSV
        </Button>
      </div>

      <div className="text-sm text-[#595959] mb-3">{details.length} reservation(s) - Total: {formatEuro(details.reduce((s, r) => s + (r.amount || 0), 0))}</div>

      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        {loading ? <div className="p-12 text-center text-[#595959]">Chargement...</div> : details.length === 0 ? (
          <div className="p-12 text-center text-[#595959]">Aucune reservation trouvee.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="text-[#2C3E30] font-semibold">Date</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Client</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Type</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Formule</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Sejour</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Montant</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Taxe sejour</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Acompte</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Solde</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Paiement</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.map(r => (
                  <TableRow key={r.id} data-testid={`detail-row-${r.id}`} className="hover:bg-[#FAFAF9]">
                    <TableCell className="text-xs">{r.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{r.name}</p>
                      <p className="text-xs text-[#595959]">{r.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs border ${r.category === 'salle' ? 'bg-[#C4A962]/10 text-[#C4A962] border-[#C4A962]/20' : 'bg-[#8A9A5B]/10 text-[#8A9A5B] border-[#8A9A5B]/20'}`}>
                        {r.category === 'salle' ? 'Salle' : 'Hebergement'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{r.accommodation_label}</TableCell>
                    <TableCell className="text-xs">{r.arrival_date} → {r.departure_date}</TableCell>
                    <TableCell className="text-right font-semibold text-sm">{formatEuro(r.amount)}</TableCell>
                    <TableCell className="text-right text-sm text-teal-700">{formatEuro(r.tourist_tax || 0)}</TableCell>
                    <TableCell className="text-right text-sm">{formatEuro(r.deposit_amount)}</TableCell>
                    <TableCell className="text-right text-sm">{formatEuro(r.balance)}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs border ${PAYMENT_STATUS[r.payment_status]?.color || 'bg-gray-100'}`}>
                        {PAYMENT_STATUS[r.payment_status]?.label || r.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-8 text-[#8A9A5B] hover:text-[#2C3E30]"
                        onClick={() => { setEditId(r.id); setEditData({ amount: r.amount, deposit_amount: r.deposit_amount, payment_status: r.payment_status }); }}>
                        <Euro className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit payment dialog */}
      <Dialog open={!!editId} onOpenChange={() => setEditId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2C3E30]">Modifier le paiement</DialogTitle>
            <DialogDescription>Mettez a jour le montant et le statut de paiement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Montant total (EUR)</label>
              <Input data-testid="edit-amount" type="number" value={editData.amount || ''} onChange={e => setEditData(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="rounded-lg h-11" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Acompte (EUR)</label>
              <Input data-testid="edit-deposit" type="number" value={editData.deposit_amount || ''} onChange={e => setEditData(p => ({ ...p, deposit_amount: parseFloat(e.target.value) || 0 }))} className="rounded-lg h-11" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Statut de paiement</label>
              <Select value={editData.payment_status} onValueChange={v => setEditData(p => ({ ...p, payment_status: v }))}>
                <SelectTrigger data-testid="edit-payment-status" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Non paye</SelectItem>
                  <SelectItem value="deposit_paid">Acompte recu</SelectItem>
                  <SelectItem value="paid">Paye</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditId(null)} className="rounded-full">Annuler</Button>
            <Button data-testid="save-payment-btn" onClick={updatePayment} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Manual Entries Tab ──────────────────────────────────
function EntriesTab({ api, onRefresh }) {
  const [entries, setEntries] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ entry_type: 'refund', amount: 0, description: '', date: format(new Date(), 'yyyy-MM-dd'), category: 'hebergement' });

  const fetchEntries = useCallback(async () => {
    try { const res = await api('get', '/api/admin/accounting/entries'); setEntries(res.data); } catch { /* */ }
  }, [api]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const addEntry = async () => {
    await api('post', '/api/admin/accounting/entry', form);
    setDialog(false);
    setForm({ entry_type: 'refund', amount: 0, description: '', date: format(new Date(), 'yyyy-MM-dd'), category: 'hebergement' });
    fetchEntries();
    onRefresh();
  };

  const deleteEntry = async (id) => {
    await api('delete', `/api/admin/accounting/entry/${id}`);
    fetchEntries();
    onRefresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#595959]">{entries.length} ecriture(s)</p>
        <Button data-testid="add-entry-btn" onClick={() => setDialog(true)} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">
          <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Ajouter une ecriture
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        {entries.length === 0 ? <div className="p-12 text-center text-[#595959]">Aucune ecriture manuelle.</div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="text-[#2C3E30] font-semibold">Date</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Type</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Description</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold">Categorie</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Montant</TableHead>
                  <TableHead className="text-[#2C3E30] font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">{e.date}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs border ${e.entry_type === 'refund' ? 'bg-red-50 text-red-700 border-red-200' : e.entry_type === 'credit' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                        {e.entry_type === 'refund' ? 'Remboursement' : e.entry_type === 'credit' ? 'Avoir' : e.entry_type === 'adjustment' ? 'Ajustement' : 'Depense'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{e.description}</TableCell>
                    <TableCell className="text-sm capitalize">{e.category}</TableCell>
                    <TableCell className={`text-right font-semibold text-sm ${e.amount < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {e.amount >= 0 ? '+' : ''}{formatEuro(e.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-8 text-red-500 hover:text-red-700" onClick={() => deleteEntry(e.id)}>
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2C3E30]">Nouvelle ecriture</DialogTitle>
            <DialogDescription>Ajoutez un remboursement, avoir ou ajustement manuel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Type</label>
              <Select value={form.entry_type} onValueChange={v => setForm(p => ({ ...p, entry_type: v }))}>
                <SelectTrigger data-testid="entry-type-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="refund">Remboursement</SelectItem>
                  <SelectItem value="credit">Avoir</SelectItem>
                  <SelectItem value="adjustment">Ajustement</SelectItem>
                  <SelectItem value="expense">Depense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Montant (EUR) - negatif pour debit</label>
              <Input data-testid="entry-amount" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="rounded-lg h-11" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Date</label>
              <Input data-testid="entry-date" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="rounded-lg h-11" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Categorie</label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hebergement">Hebergement</SelectItem>
                  <SelectItem value="salle">Salle des fetes</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea data-testid="entry-description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Ex: Remboursement partiel Famille X..." className="rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="rounded-full">Annuler</Button>
            <Button data-testid="save-entry-btn" onClick={addEntry} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Taxe sejour Tab ─────────────────────────────────────
function TaxeSejourTab({ api }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState('all');
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [message, setMessage] = useState('');
  const [reminders, setReminders] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const url = year === 'all' ? '/api/admin/accounting/tourist-tax-detail' : `/api/admin/accounting/tourist-tax-detail?year=${year}`;
      const [res, remRes] = await Promise.all([
        api('get', url),
        api('get', '/api/admin/recurring-tasks'),
      ]);
      setData(res.data);
      setReminders(remRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [api, year]);
  useEffect(() => { fetch(); }, [fetch]);

  const recompute = async () => {
    setRecomputing(true);
    setMessage('');
    try {
      const res = await api('post', '/api/admin/tourist-tax/recompute');
      setMessage(`${res.data.updated} reservations recalculees sur ${res.data.total_direct} directes.`);
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setRecomputing(false);
    }
  };

  const years = ['all', 2024, 2025, 2026, 2027];
  const formatEuro = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
  const exemptCounts = {};
  (data?.exempt_reservations || []).forEach(e => { exemptCounts[e.exempt_reason] = (exemptCounts[e.exempt_reason] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="taxe-sejour-tab">
      {/* Header + controls */}
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-[#2C3E30] text-lg">Taxe de sejour — Saint-Etienne Metropole</h2>
          <p className="text-xs text-[#595959] mt-1">
            Formule : <code className="bg-[#F5F5F0] px-1 rounded">adultes × nuits × min(prix_HT / nuits / adultes × 5%, 4,90€)</code> · Majoration +10% depuis {data?.dept_majoration_start || '2026-01-01'}
          </p>
          <p className="text-xs text-[#595959] mt-1">
            Sources concernees : {(() => {
              const srcs = data?.direct_sources || ['Site direct', 'Direct', 'Téléphone', 'Manuel', 'Externe'];
              // Deduplicate accented variants (Téléphone / Telephone)
              const norm = new Map();
              srcs.forEach(s => { const key = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(); if (!norm.has(key)) norm.set(key, s); });
              return [...norm.values()].join(', ');
            })()} · Plateformes externes exemptees.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={v => setYear(v === 'all' ? 'all' : parseInt(v))}>
            <SelectTrigger data-testid="taxe-year-select" className="w-32 rounded-full border-[#E5E5E0]"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y === 'all' ? 'Toutes' : y}</SelectItem>)}</SelectContent>
          </Select>
          <Button
            data-testid="taxe-recompute-btn"
            onClick={recompute}
            disabled={recomputing}
            variant="outline"
            className="rounded-full text-sm border-[#E5E5E0] h-10"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${recomputing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
            Recalculer
          </Button>
          <a
            href="https://stetiennemetropole.taxesejour.fr/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="taxe-declare-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium px-4 h-10 rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]"
          >
            Declarer <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </div>
      </div>

      {message && (
        <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200" data-testid="taxe-message">{message}</div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-teal-50 border border-teal-200">
          <p className="text-xs uppercase tracking-wider text-teal-700 mb-1">Taxe totale</p>
          <p className="text-2xl font-light text-teal-900" data-testid="taxe-total">{formatEuro(data?.total_tax)}</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
          <p className="text-xs uppercase tracking-wider text-emerald-700 mb-1">Reservations concernees</p>
          <p className="text-2xl font-light text-emerald-900" data-testid="taxe-count-active">{data?.count_active || 0}</p>
        </div>
        <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]">
          <p className="text-xs uppercase tracking-wider text-[#8A8A85] mb-1">Exemptees</p>
          <p className="text-2xl font-light text-[#1A1C19]" data-testid="taxe-count-exempt">{data?.count_exempt || 0}</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-xs uppercase tracking-wider text-amber-700 mb-1">Plafond / adulte / nuit</p>
          <p className="text-2xl font-light text-amber-900">{formatEuro(data?.cap)}</p>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="p-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-semibold text-[#2C3E30]">Detail par reservation ({data?.count_active || 0})</h3>
          <p className="text-xs text-[#595959]">Justificatif prêt pour la declaration.</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F5F0]">
                <TableHead className="text-xs">Client</TableHead>
                <TableHead className="text-xs">Dates</TableHead>
                <TableHead className="text-xs text-right">Adultes × Nuits</TableHead>
                <TableHead className="text-xs text-right">Prix HT / nuit / adulte</TableHead>
                <TableHead className="text-xs text-right">Taux 5%</TableHead>
                <TableHead className="text-xs text-right">Applique / pers / nuit</TableHead>
                <TableHead className="text-xs text-center">+10% dept.</TableHead>
                <TableHead className="text-xs text-right">Taxe totale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#595959]">Chargement...</TableCell></TableRow>
              ) : (data?.active_reservations || []).length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#595959]">Aucune reservation directe soumise a la taxe.</TableCell></TableRow>
              ) : (
                (data?.active_reservations || []).map(r => (
                  <TableRow key={r.reservation_id} data-testid={`taxe-row-${r.reservation_id}`} className="hover:bg-[#FAFAF9]">
                    <TableCell className="text-sm py-2">
                      <div className="font-medium text-[#2C3E30]">{r.name}</div>
                      <div className="text-xs text-[#8A8A85]">{r.source}</div>
                    </TableCell>
                    <TableCell className="text-xs py-2">{r.arrival_date} → {r.departure_date}</TableCell>
                    <TableCell className="text-right text-sm py-2">{r.adults} × {r.nights}</TableCell>
                    <TableCell className="text-right text-sm py-2">{formatEuro(r.prix_ht_pn_pers)}</TableCell>
                    <TableCell className="text-right text-sm py-2">{formatEuro(r.rate_5pct)}</TableCell>
                    <TableCell className="text-right text-sm py-2">
                      {formatEuro(r.applied_pn_pers)}
                      {r.capped && <Badge className="ml-1.5 bg-amber-100 text-amber-800 border-amber-200 border text-[10px]">plafond</Badge>}
                    </TableCell>
                    <TableCell className="text-center text-sm py-2">
                      {r.dept_majoration_applied ? <Badge className="bg-teal-100 text-teal-800 border-teal-200 border text-[10px]">+10%</Badge> : <span className="text-[#8A8A85] text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm py-2 font-semibold text-teal-700">{formatEuro(r.tax)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Exempt summary */}
      {Object.keys(exemptCounts).length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="taxe-exempt-summary">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-[#8A8A85]" strokeWidth={1.5} />
            <h3 className="font-semibold text-[#2C3E30]">Reservations exemptees ({data?.count_exempt || 0})</h3>
          </div>
          <ul className="text-sm text-[#595959] space-y-1.5 pl-6">
            {Object.entries(exemptCounts).map(([reason, count]) => (
              <li key={reason} className="list-disc"><strong>{count}</strong> — {reason}</li>
            ))}
          </ul>
          <p className="text-xs text-[#8A8A85] mt-3">
            Les reservations Airbnb / Booking sont exemptees : ces plateformes collectent et reversent la taxe elles-memes.
          </p>
        </div>
      )}

      {/* Monthly declaration reminders (auto-inserted on the 10th of each month) */}
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="taxe-monthly-reminders">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-[#2C3E30]">Rappels mensuels de declaration</h3>
            <p className="text-xs text-[#595959] mt-1">
              Inseres automatiquement le 10 de chaque mois avec un lien direct vers le portail Saint-Etienne Metropole.
            </p>
          </div>
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 border text-[10px]">Automatique</Badge>
        </div>
        {reminders.length === 0 ? (
          <p className="text-xs text-[#8A8A85] italic">Aucun rappel actif pour le moment. Le prochain sera insere le 10 du mois prochain.</p>
        ) : (
          <ul className="space-y-2" data-testid="taxe-reminders-list">
            {reminders.slice(0, 12).map(r => (
              <li key={r.id} data-testid={`taxe-reminder-${r.recurring_key}`} className="flex items-center justify-between p-2 rounded-lg bg-[#FAFAF8] border border-[#E5E5E0]">
                <div>
                  <p className="text-sm text-[#2C3E30]">{r.title}</p>
                  <p className="text-xs text-[#8A8A85]">Cree le {r.due_date}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.completed ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Fait</Badge> : <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]">A faire</Badge>}
                  {r.link && (
                    <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2C3E30] underline hover:no-underline">Ouvrir</a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Obligations Administratives Tab ─────────────────────
const ALERT_STYLES = {
  overdue: { label: 'Echu', badge: 'bg-red-100 text-red-800 border-red-300 animate-pulse', card: 'border-red-300 bg-red-50/40', icon: 'text-red-600' },
  j7: { label: 'J-7', badge: 'bg-red-100 text-red-700 border-red-200', card: 'border-red-200 bg-red-50/30', icon: 'text-red-500' },
  j30: { label: 'J-30', badge: 'bg-orange-100 text-orange-700 border-orange-200', card: 'border-orange-200 bg-orange-50/30', icon: 'text-orange-500' },
  upcoming: { label: 'A venir', badge: 'bg-sky-50 text-sky-700 border-sky-200', card: 'border-[#E5E5E0]', icon: 'text-sky-500' },
  paid: { label: 'Paye', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', card: 'border-emerald-200 bg-emerald-50/20', icon: 'text-emerald-500' },
  skipped: { label: 'Non applicable', badge: 'bg-neutral-100 text-neutral-600 border-neutral-200', card: 'border-neutral-200', icon: 'text-neutral-500' },
};

const FREQ_LABELS = { monthly: 'Mensuelle', quarterly: 'Trimestrielle', annual: 'Annuelle' };

function ObligationsTab({ api }) {
  const [obligations, setObligations] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [recapLog, setRecapLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [markModal, setMarkModal] = useState(null); // {template_id, due_date, title}
  const [markForm, setMarkForm] = useState({ paid_amount: '', notes: '' });
  const [form, setForm] = useState({ title: '', frequency: 'annual', due_day: 15, due_months: '', estimated_amount: '', category: '', link: '', notes: '' });
  const token = localStorage.getItem('admin_token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ob, up, lg] = await Promise.all([
        api('get', '/api/admin/obligations'),
        api('get', '/api/admin/obligations/upcoming?days=30'),
        api('get', '/api/admin/obligations/recap-log'),
      ]);
      setObligations(ob.data || []);
      setUpcoming(up.data || []);
      setRecapLog(lg.data || []);
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => setForm({ title: '', frequency: 'annual', due_day: 15, due_months: '', estimated_amount: '', category: '', link: '', notes: '' });
  const openCreate = () => { resetForm(); setEditing(null); setShowForm(true); };
  const openEdit = tpl => {
    setEditing(tpl);
    setForm({
      title: tpl.title, frequency: tpl.frequency, due_day: tpl.due_day,
      due_months: (tpl.due_months || []).join(','), estimated_amount: tpl.estimated_amount ?? '',
      category: tpl.category || '', link: tpl.link || '', notes: tpl.notes || '',
    });
    setShowForm(true);
  };
  const saveObligation = async () => {
    const payload = {
      title: form.title, frequency: form.frequency, due_day: parseInt(form.due_day || 1, 10),
      due_months: form.due_months ? form.due_months.split(',').map(x => parseInt(x.trim(), 10)).filter(Boolean) : null,
      estimated_amount: form.estimated_amount === '' ? null : parseFloat(form.estimated_amount),
      category: form.category || null, link: form.link || null, notes: form.notes || null,
      auto_amount: null,
    };
    try {
      if (editing) {
        await api('put', `/api/admin/obligations/${editing.id}`, payload);
        setMessage('Obligation mise a jour.');
      } else {
        await api('post', '/api/admin/obligations', payload);
        setMessage('Obligation creee.');
      }
      setShowForm(false); setEditing(null); resetForm();
      await fetchAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de l\'enregistrement.');
    }
  };
  const remove = async (tpl) => {
    if (!window.confirm(tpl.is_default ? 'Cette obligation par defaut sera desactivee (non supprimee). Continuer ?' : 'Supprimer definitivement cette obligation et son historique ?')) return;
    await api('delete', `/api/admin/obligations/${tpl.id}`);
    setMessage(tpl.is_default ? 'Obligation par defaut desactivee.' : 'Obligation supprimee.');
    await fetchAll();
  };
  const openMark = (tpl, occ) => {
    setMarkModal({ template_id: tpl.id, due_date: occ.due_date, title: tpl.title, estimated: occ.auto_amount ?? tpl.estimated_amount });
    setMarkForm({ paid_amount: (occ.auto_amount ?? tpl.estimated_amount) ?? '', notes: '' });
  };
  const submitMark = async (status) => {
    await api('post', '/api/admin/obligations/mark', {
      template_id: markModal.template_id,
      due_date: markModal.due_date,
      paid_amount: markForm.paid_amount === '' ? null : parseFloat(markForm.paid_amount),
      notes: markForm.notes || null,
      status,
    });
    setMarkModal(null); setMarkForm({ paid_amount: '', notes: '' });
    setMessage(`Obligation ${status === 'paid' ? 'marquee comme payee' : status === 'skipped' ? 'marquee non applicable' : 'reinitialisee'}.`);
    await fetchAll();
  };
  const resetOccurrence = async (tpl, occ) => {
    await api('post', '/api/admin/obligations/mark', {
      template_id: tpl.id, due_date: occ.due_date, status: 'pending',
    });
    setMessage('Obligation reinitialisee.');
    await fetchAll();
  };
  const triggerRecap = async () => {
    if (!window.confirm('Envoyer le recapitulatif comptable du mois par email maintenant ?')) return;
    setMessage('Envoi en cours...');
    try {
      const res = await api('post', '/api/admin/obligations/monthly-recap-now');
      const log = res.data?.log || {};
      setMessage(`Recap envoye (${log.status || 'ok'}) — destinataire: ${log.recipient || 'ADMIN_EMAIL'}, ${log.invoice_count || 0} factures jointes.`);
      await fetchAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de l\'envoi du recap.');
    }
  };
  const formatEuro = n => n == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
  const nextOccurrence = tpl => tpl.occurrences.find(o => o.status !== 'paid') || tpl.occurrences[0];
  const lastPaid = tpl => [...tpl.occurrences].reverse().find(o => o.status === 'paid');

  return (
    <div className="space-y-6" data-testid="obligations-tab">
      {message && <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200" data-testid="obligations-message">{message}</div>}

      {/* Upcoming panel — top summary */}
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="obligations-upcoming-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
            <h2 className="font-semibold text-[#2C3E30] text-lg">Prochaines echeances (30 j)</h2>
            <Badge className="bg-[#F5F5F0] text-[#2C3E30] border-none text-[10px]" data-testid="obligations-upcoming-count">{upcoming.length}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="obligations-recap-now-btn" onClick={triggerRecap} variant="outline" className="rounded-full border-[#C4A962] text-[#8A6E2F] hover:bg-[#C4A962]/10 text-sm h-9">
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Envoyer recap mensuel maintenant
            </Button>
            <Button data-testid="obligations-add-btn" onClick={openCreate} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] text-sm h-9">
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Nouvelle echeance
            </Button>
          </div>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-sm text-[#8A8A85] italic">Aucune echeance dans les 30 prochains jours. Tout est a jour.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcoming.slice(0, 12).map(u => {
              const st = ALERT_STYLES[u.alert_level] || ALERT_STYLES.upcoming;
              return (
                <div key={`${u.template_id}_${u.due_date}`} data-testid={`upcoming-${u.template_id}-${u.due_date}`} className={`p-3 rounded-lg border ${st.card} flex items-center justify-between`}>
                  <div>
                    <p className="text-sm font-medium text-[#2C3E30] leading-tight">{u.title}</p>
                    <p className="text-xs text-[#595959] mt-0.5">{u.due_date} • {formatEuro(u.estimated_amount)}</p>
                  </div>
                  <Badge className={`${st.badge} border text-[10px]`}>{st.label}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full obligations grid */}
      <div className="space-y-4">
        <h2 className="font-semibold text-[#2C3E30] text-lg flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[#2C3E30]" strokeWidth={1.5} />
          Toutes mes obligations
          {loading && <RefreshCw className="w-4 h-4 animate-spin text-[#8A8A85]" strokeWidth={1.5} />}
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {obligations.map(tpl => {
            const nx = nextOccurrence(tpl);
            const lp = lastPaid(tpl);
            const st = ALERT_STYLES[nx?.alert_level] || ALERT_STYLES.upcoming;
            const isTva = tpl.key === 'tva_ca3';
            return (
              <div key={tpl.id} data-testid={`obligation-card-${tpl.key || tpl.id}`} className={`p-5 rounded-xl bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] border-2 ${st.card}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#2C3E30] text-base leading-tight">{tpl.title}</h3>
                      {tpl.category && <Badge className="bg-[#F5F5F0] text-[#595959] border-none text-[10px]">{tpl.category}</Badge>}
                      {tpl.is_default && <Badge className="bg-neutral-100 text-neutral-600 border-none text-[10px]">Predefinie</Badge>}
                    </div>
                    <p className="text-xs text-[#8A8A85] mt-1">{FREQ_LABELS[tpl.frequency] || tpl.frequency} • jour {tpl.due_day}{tpl.due_months?.length ? ` (mois ${tpl.due_months.join(', ')})` : ''}</p>
                  </div>
                  {nx && <Badge className={`${st.badge} border text-[10px] shrink-0`} data-testid={`obligation-badge-${tpl.key || tpl.id}`}>{st.label}</Badge>}
                </div>

                {tpl.notes && <p className="text-xs text-[#595959] italic mb-3">{tpl.notes}</p>}

                {nx && (
                  <div className="p-3 rounded-lg bg-[#FAFAF8] border border-[#E5E5E0] mb-3">
                    <p className="text-xs uppercase text-[#8A8A85] mb-0.5">Prochaine echeance</p>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <p className="text-lg font-light text-[#2C3E30] font-mono">{nx.due_date}</p>
                      <p className="text-sm font-medium text-[#2C3E30]" data-testid={`obligation-amount-${tpl.key || tpl.id}`}>
                        {tpl.auto_amount ? `Auto : ${formatEuro(nx.auto_amount)}` : formatEuro(tpl.estimated_amount)}
                      </p>
                    </div>
                  </div>
                )}

                {lp && (
                  <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-lg bg-emerald-50/60 border border-emerald-100" data-testid={`obligation-lastpaid-${tpl.key || tpl.id}`}>
                    <p className="text-xs text-emerald-800">
                      <CheckCircle2 className="w-3 h-3 inline mr-1" strokeWidth={2} />
                      Dernier paiement : <span className="font-mono">{lp.due_date}</span>
                      {lp.paid_amount != null && ` — ${formatEuro(lp.paid_amount)}`}
                    </p>
                    <button data-testid={`obligation-reset-lastpaid-${tpl.key || tpl.id}`} onClick={() => resetOccurrence(tpl, lp)} className="text-[11px] text-emerald-700 hover:underline">Reouvrir</button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {nx && nx.status === 'pending' && (
                    <Button size="sm" data-testid={`obligation-mark-paid-${tpl.key || tpl.id}`} onClick={() => openMark(tpl, nx)} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 h-8 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Marquer comme payee
                    </Button>
                  )}
                  {nx && nx.status === 'paid' && (
                    <Button size="sm" data-testid={`obligation-reset-${tpl.key || tpl.id}`} onClick={() => resetOccurrence(tpl, nx)} variant="outline" className="rounded-full h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      <RefreshCw className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Reouvrir
                    </Button>
                  )}
                  {isTva && (
                    <a data-testid={`obligation-ca3-${tpl.key}`} href={`${BACKEND_URL}/api/admin/obligations/tva-ca3?month=${(nx?.due_date || '').slice(0, 7)}&token=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium px-3 h-8 rounded-full bg-[#C4A962] text-white hover:bg-[#b5945a]">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Generer declaration CA3
                    </a>
                  )}
                  {tpl.link && (
                    <a href={tpl.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium px-3 h-8 rounded-full border border-[#E5E5E0] text-[#2C3E30] hover:bg-[#F5F5F0]">
                      <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> Portail
                    </a>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(tpl)} data-testid={`obligation-edit-${tpl.key || tpl.id}`} className="rounded-full h-8 text-xs text-[#595959]">Modifier</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(tpl)} data-testid={`obligation-delete-${tpl.key || tpl.id}`} className="rounded-full h-8 text-xs text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                </div>
              </div>
            );
          })}
          {obligations.length === 0 && !loading && (
            <p className="text-sm text-[#8A8A85] italic col-span-2">Aucune obligation. Cliquez sur &laquo;&nbsp;Nouvelle echeance&nbsp;&raquo; pour en ajouter.</p>
          )}
        </div>
      </div>

      {/* Monthly recap history */}
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="obligations-recap-history">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-[#2C3E30] text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} />
            Historique des recapitulatifs mensuels
          </h2>
          <Badge className="bg-[#F5F5F0] text-[#2C3E30] border-none text-[10px]">Automatique le 1er du mois</Badge>
        </div>
        {recapLog.length === 0 ? (
          <p className="text-sm text-[#8A8A85] italic">Aucun recap envoye pour le moment. Il sera envoye automatiquement le 1er du mois prochain.</p>
        ) : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-[#F5F5F0]">
              <TableHead className="text-xs">Envoye le</TableHead>
              <TableHead className="text-xs">Periode couverte</TableHead>
              <TableHead className="text-xs">CA</TableHead>
              <TableHead className="text-xs">TVA nette</TableHead>
              <TableHead className="text-xs">Taxe sejour</TableHead>
              <TableHead className="text-xs">Factures</TableHead>
              <TableHead className="text-xs">Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recapLog.slice(0, 24).map(r => (
                <TableRow key={r.id} data-testid={`recap-row-${r.month}`} className="hover:bg-[#FAFAF9]">
                  <TableCell className="text-xs font-mono">{(r.sent_at || '').slice(0, 10)}</TableCell>
                  <TableCell className="text-xs font-mono">{r.prev_month}</TableCell>
                  <TableCell className="text-xs">{formatEuro(r.ca_total)}</TableCell>
                  <TableCell className={`text-xs ${r.tva_net < 0 ? 'text-orange-700' : 'text-[#2C3E30]'}`}>{formatEuro(r.tva_net)}</TableCell>
                  <TableCell className="text-xs">{formatEuro(r.taxe_sejour)}</TableCell>
                  <TableCell className="text-xs">{r.invoice_count}</TableCell>
                  <TableCell className="text-xs">
                    {r.status === 'sent' ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Envoye</Badge>
                     : r.status === 'error' ? <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px]" title={r.error}>Erreur</Badge>
                     : <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200 border text-[10px]">{r.status}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        )}
      </div>

      {/* Create / Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="obligation-form-modal" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-[#2C3E30] text-lg mb-4">{editing ? 'Modifier l\'obligation' : 'Nouvelle obligation'}</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-[#595959] block mb-1">Titre</label><Input data-testid="obligation-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#595959] block mb-1">Frequence</label>
                  <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="obligation-form-frequency" className="rounded-lg border-[#E5E5E0]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuelle</SelectItem>
                      <SelectItem value="quarterly">Trimestrielle</SelectItem>
                      <SelectItem value="annual">Annuelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-[#595959] block mb-1">Jour du mois</label><Input type="number" min="1" max="31" data-testid="obligation-form-day" value={form.due_day} onChange={e => setForm({ ...form, due_day: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              </div>
              {(form.frequency === 'quarterly' || form.frequency === 'annual') && (
                <div><label className="text-xs text-[#595959] block mb-1">Mois concernes (1-12, separes par virgule)</label><Input data-testid="obligation-form-months" placeholder="ex : 1,4,7,10 ou 12" value={form.due_months} onChange={e => setForm({ ...form, due_months: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#595959] block mb-1">Montant estime (EUR)</label><Input type="number" step="0.01" data-testid="obligation-form-amount" value={form.estimated_amount} onChange={e => setForm({ ...form, estimated_amount: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
                <div><label className="text-xs text-[#595959] block mb-1">Categorie</label><Input data-testid="obligation-form-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              </div>
              <div><label className="text-xs text-[#595959] block mb-1">Lien portail (optionnel)</label><Input data-testid="obligation-form-link" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">Notes</label><textarea data-testid="obligation-form-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" data-testid="obligation-form-cancel" onClick={() => { setShowForm(false); setEditing(null); resetForm(); }} className="rounded-full">Annuler</Button>
              <Button data-testid="obligation-form-save" disabled={!form.title} onClick={saveObligation} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as paid modal */}
      {markModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="obligation-mark-modal" onClick={() => setMarkModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-[#2C3E30] text-lg mb-2">Marquer comme payee</h3>
            <p className="text-sm text-[#595959] mb-4">{markModal.title} — echeance {markModal.due_date}</p>
            <div className="space-y-3">
              <div><label className="text-xs text-[#595959] block mb-1">Montant paye (EUR)</label><Input type="number" step="0.01" data-testid="obligation-mark-amount" value={markForm.paid_amount} onChange={e => setMarkForm({ ...markForm, paid_amount: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">Reference / notes (optionnel)</label><textarea data-testid="obligation-mark-notes" value={markForm.notes} onChange={e => setMarkForm({ ...markForm, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-between gap-2 mt-5">
              <Button variant="ghost" data-testid="obligation-mark-skip" onClick={() => submitMark('skipped')} className="rounded-full text-neutral-600 text-xs">Non applicable</Button>
              <div className="flex gap-2">
                <Button variant="outline" data-testid="obligation-mark-cancel" onClick={() => setMarkModal(null)} className="rounded-full">Annuler</Button>
                <Button data-testid="obligation-mark-confirm" onClick={() => submitMark('paid')} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                  <CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Confirmer paiement
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Expenses (Charges) Tab ──────────────────────────────
function ExpensesTab({ api }) {
  const [data, setData] = useState({ expenses: [], total_ht: 0, total_tva: 0, total_ttc: 0, count: 0 });
  const [categories, setCategories] = useState([]);
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');
  const [category, setCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', supplier: '', invoice_number: '', amount_ht: '', tva_rate: '20', amount_ttc: '', category: '', notes: '' });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('admin_token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetch = useCallback(async () => {
    const params = new URLSearchParams();
    if (year !== 'all') params.set('year', year);
    if (month !== 'all') params.set('month', month);
    if (category !== 'all') params.set('category', category);
    const [d, c] = await Promise.all([
      api('get', `/api/admin/expenses?${params}`),
      api('get', '/api/admin/expenses/categories'),
    ]);
    setData(d.data);
    setCategories(c.data);
  }, [api, year, month, category]);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => setForm({ date: '', supplier: '', invoice_number: '', amount_ht: '', tva_rate: '20', amount_ttc: '', category: '', notes: '' });

  const openCreate = () => { setEditing(null); resetForm(); setShowForm(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ date: e.date, supplier: e.supplier, invoice_number: e.invoice_number || '', amount_ht: String(e.amount_ht), tva_rate: String(e.tva_rate), amount_ttc: String(e.amount_ttc), category: e.category, notes: e.notes || '' });
    setShowForm(true);
  };

  const onHtChange = (v) => {
    setForm(f => {
      const ht = parseFloat(v || 0);
      const tva = parseFloat(f.tva_rate || 20);
      const ttc = ht * (1 + tva / 100);
      return { ...f, amount_ht: v, amount_ttc: ttc.toFixed(2) };
    });
  };
  const onRateChange = (v) => {
    setForm(f => {
      const ht = parseFloat(f.amount_ht || 0);
      const rate = parseFloat(v || 0);
      const ttc = ht * (1 + rate / 100);
      return { ...f, tva_rate: v, amount_ttc: ttc.toFixed(2) };
    });
  };

  const save = async (uploadFile) => {
    setSaving(true);
    setMessage('');
    try {
      const payload = { ...form, amount_ht: parseFloat(form.amount_ht || 0), tva_rate: parseFloat(form.tva_rate || 0), amount_ttc: parseFloat(form.amount_ttc || 0) };
      let saved;
      if (editing) {
        await api('put', `/api/admin/expenses/${editing.id}`, payload);
        saved = { id: editing.id };
      } else {
        const res = await api('post', '/api/admin/expenses', payload);
        saved = res.data;
      }
      if (uploadFile) {
        const fd = new FormData();
        fd.append('file', uploadFile);
        await api('post', `/api/admin/expenses/${saved.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setMessage(editing ? 'Charge mise a jour.' : 'Charge enregistree.');
      setShowForm(false);
      setEditing(null);
      resetForm();
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer definitivement cette charge ?')) return;
    await api('delete', `/api/admin/expenses/${id}`);
    await fetch();
  };

  const formatEuro = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const months = [{ v: 'all', l: 'Tous' }, ...Array.from({length: 12}, (_, i) => ({ v: String(i + 1), l: String(i + 1).padStart(2, '0') }))];

  return (
    <div className="space-y-6" data-testid="expenses-tab">
      {message && <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200" data-testid="expenses-message">{message}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(year)} onValueChange={v => setYear(v === 'all' ? 'all' : parseInt(v))}>
          <SelectTrigger data-testid="expenses-year" className="w-28 rounded-full border-[#E5E5E0] h-9 text-sm"><SelectValue placeholder="Annee" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes</SelectItem>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(month)} onValueChange={v => setMonth(v === 'all' ? 'all' : parseInt(v))}>
          <SelectTrigger data-testid="expenses-month" className="w-28 rounded-full border-[#E5E5E0] h-9 text-sm"><SelectValue placeholder="Mois" /></SelectTrigger>
          <SelectContent>{months.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="expenses-category-filter" className="w-64 rounded-full border-[#E5E5E0] h-9 text-sm"><SelectValue placeholder="Categorie" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes categories</SelectItem>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="expenses-add-btn" onClick={openCreate} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] text-sm h-9">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Ajouter une charge
        </Button>
        <a data-testid="expenses-csv-export" href={`${BACKEND_URL}/api/admin/expenses/export/csv?token=${encodeURIComponent(token)}${year !== 'all' ? `&year=${year}` : ''}`} className="inline-flex items-center gap-1.5 text-sm px-4 h-9 rounded-full border border-[#E5E5E0] text-[#2C3E30] hover:bg-[#F5F5F0]">
          <FileDown className="w-4 h-4" strokeWidth={1.5} /> Export CSV
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]"><p className="text-xs uppercase text-[#8A8A85] mb-1">Nombre</p><p className="text-2xl font-light" data-testid="expenses-count">{data.count}</p></div>
        <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]"><p className="text-xs uppercase text-[#8A8A85] mb-1">Total HT</p><p className="text-2xl font-light" data-testid="expenses-total-ht">{formatEuro(data.total_ht)}</p></div>
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200"><p className="text-xs uppercase text-emerald-700 mb-1">TVA deductible</p><p className="text-2xl font-light text-emerald-900" data-testid="expenses-total-tva">{formatEuro(data.total_tva)}</p></div>
        <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]"><p className="text-xs uppercase text-[#8A8A85] mb-1">Total TTC</p><p className="text-2xl font-light" data-testid="expenses-total-ttc">{formatEuro(data.total_ttc)}</p></div>
      </div>

      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow className="bg-[#F5F5F0]">
            <TableHead className="text-xs">Date</TableHead>
            <TableHead className="text-xs">Fournisseur</TableHead>
            <TableHead className="text-xs">N° facture</TableHead>
            <TableHead className="text-xs">Categorie</TableHead>
            <TableHead className="text-xs text-right">HT</TableHead>
            <TableHead className="text-xs text-right">TVA</TableHead>
            <TableHead className="text-xs text-right">TTC</TableHead>
            <TableHead className="text-xs">PDF</TableHead>
            <TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.expenses.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-6 text-sm text-[#595959]">Aucune charge enregistree.</TableCell></TableRow>
             : data.expenses.map(e => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-[#FAFAF9]">
                <TableCell className="text-xs py-2">{e.date}</TableCell>
                <TableCell className="text-sm py-2">{e.supplier}</TableCell>
                <TableCell className="text-xs py-2 font-mono">{e.invoice_number}</TableCell>
                <TableCell className="text-xs py-2"><Badge className="bg-[#F5F5F0] text-[#2C3E30] border-[#E5E5E0] border text-[10px]">{e.category}</Badge></TableCell>
                <TableCell className="text-right text-sm py-2">{formatEuro(e.amount_ht)}</TableCell>
                <TableCell className="text-right text-sm py-2 text-emerald-700">{formatEuro(e.amount_tva)}</TableCell>
                <TableCell className="text-right text-sm py-2 font-medium">{formatEuro(e.amount_ttc)}</TableCell>
                <TableCell className="text-xs py-2">
                  {e.pdf_filename ? <a href={`${BACKEND_URL}/api/admin/expenses/${e.id}/pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer" className="text-[#2C3E30] underline">Ouvrir</a> : <span className="text-[#8A8A85]">—</span>}
                </TableCell>
                <TableCell className="text-right py-2">
                  <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full" data-testid={`expense-edit-${e.id}`} onClick={() => openEdit(e)}>Modifier</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-red-600 hover:text-red-700" data-testid={`expense-delete-${e.id}`} onClick={() => remove(e.id)}><Trash2 className="w-3 h-3" strokeWidth={1.5} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="expense-form-modal" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-[#2C3E30] text-lg mb-4">{editing ? 'Modifier une charge' : 'Nouvelle charge'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[#595959] block mb-1">Date</label><Input type="date" data-testid="expense-form-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">Fournisseur</label><Input data-testid="expense-form-supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">N° facture</label><Input data-testid="expense-form-invoice" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">Categorie</label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-form-category" className="rounded-lg border-[#E5E5E0]"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-[#595959] block mb-1">HT (EUR)</label><Input type="number" step="0.01" data-testid="expense-form-ht" value={form.amount_ht} onChange={e => onHtChange(e.target.value)} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">TVA %</label><Input type="number" step="0.1" data-testid="expense-form-rate" value={form.tva_rate} onChange={e => onRateChange(e.target.value)} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">TTC (EUR)</label><Input type="number" step="0.01" data-testid="expense-form-ttc" value={form.amount_ttc} onChange={e => setForm({ ...form, amount_ttc: e.target.value })} className="rounded-lg border-[#E5E5E0]" /></div>
              <div><label className="text-xs text-[#595959] block mb-1">PDF (optionnel)</label><input type="file" accept=".pdf,application/pdf" data-testid="expense-form-pdf" id="expense-form-pdf-input" className="text-xs" /></div>
              <div className="md:col-span-2"><label className="text-xs text-[#595959] block mb-1">Notes</label><textarea data-testid="expense-form-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" data-testid="expense-form-cancel" onClick={() => { setShowForm(false); setEditing(null); resetForm(); }} className="rounded-full">Annuler</Button>
              <Button data-testid="expense-form-save" disabled={saving || !form.date || !form.supplier || !form.category || !form.amount_ht} onClick={() => { const f = document.getElementById('expense-form-pdf-input'); save(f?.files?.[0]); }} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VAT (TVA nette) Tab ─────────────────────────────────
function VatTab({ api }) {
  const [data, setData] = useState({ months: [], declare_url: '' });
  useEffect(() => { api('get', '/api/admin/vat/monthly').then(r => setData(r.data)); }, [api]);
  const formatEuro = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  return (
    <div className="space-y-6" data-testid="vat-tab">
      <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-semibold text-[#2C3E30] text-lg">TVA nette a reverser (CA3)</h2>
          <p className="text-xs text-[#595959] mt-1">
            TVA nette = <strong>TVA collectee</strong> (reservations directes, taux 20%) − <strong>TVA deductible</strong> (charges saisies).
            Un solde negatif indique un <strong>credit de TVA</strong> remboursable.
          </p>
        </div>
        <a data-testid="vat-declare-link" href={data.declare_url || 'https://cfspro.impots.gouv.fr'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium px-4 h-10 rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">Declarer sur impots.gouv.fr <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /></a>
      </div>
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow className="bg-[#F5F5F0]">
            <TableHead className="text-xs">Mois</TableHead>
            <TableHead className="text-xs text-right">TVA collectee</TableHead>
            <TableHead className="text-xs text-right">TVA deductible</TableHead>
            <TableHead className="text-xs text-right">TVA nette</TableHead>
            <TableHead className="text-xs text-center">Statut</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.months.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-sm text-[#595959]">Aucune donnee.</TableCell></TableRow>
             : data.months.map(m => (
              <TableRow key={m.month} data-testid={`vat-row-${m.month}`} className="hover:bg-[#FAFAF9]">
                <TableCell className="text-sm py-2 font-mono">{m.month}</TableCell>
                <TableCell className="text-right text-sm py-2 text-teal-700">{formatEuro(m.collected)}</TableCell>
                <TableCell className="text-right text-sm py-2 text-emerald-700">{formatEuro(m.deductible)}</TableCell>
                <TableCell className={`text-right text-sm py-2 font-semibold ${m.credit ? 'text-orange-700' : 'text-[#2C3E30]'}`}>{formatEuro(Math.abs(m.net))}</TableCell>
                <TableCell className="text-center py-2">
                  {m.credit ? <Badge className="bg-orange-50 text-orange-700 border-orange-200 border text-[10px]">Credit de TVA</Badge> : <Badge className="bg-teal-50 text-teal-700 border-teal-200 border text-[10px]">A reverser</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></div>
      </div>
    </div>
  );
}

// ─── Invoices Tab ────────────────────────────────────────
function InvoicesTab({ api }) {
  const [invoices, setInvoices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState({ id: null, kind: null });
  const [message, setMessage] = useState('');
  const [yearFilter, setYearFilter] = useState('all');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, resvRes, setRes] = await Promise.all([
        api('get', '/api/admin/invoices'),
        api('get', '/api/admin/reservations-unified'),
        api('get', '/api/admin/settings'),
      ]);
      setInvoices(invRes.data || []);
      setReservations(resvRes.data || []);
      setSettings(setRes.data || {});
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { fetch(); }, [fetch]);

  const token = localStorage.getItem('admin_token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const isDirect = r => ['Site direct', 'Direct', 'Site', 'Téléphone', 'Telephone', 'Manuel', 'Externe'].includes(r?.source);
  const invoicedIds = new Set(invoices.filter(i => !i.is_rectificative).map(i => i.reservation_id));
  const eligible = reservations.filter(r => r.entry_type === 'reservation' && isDirect(r) && r.status !== 'cancelled' && !invoicedIds.has(r.id));

  const siretMissing = !settings?.billing_siret;
  const formatEuro = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const generate = async (reservationId) => {
    setAction({ id: reservationId, kind: 'gen' });
    setMessage('');
    try {
      const res = await api('post', `/api/admin/invoices/generate/${reservationId}`);
      setMessage(`Facture ${res.data.invoice_number} generee${res.data.is_draft ? ' (BROUILLON)' : ''}.`);
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setAction({ id: null, kind: null });
    }
  };

  const generateBatch = async () => {
    if (!window.confirm(`Generer les ${eligible.length} factures manquantes ? Cette action est irreversible.`)) return;
    setAction({ id: 'batch', kind: 'batch' });
    setMessage('');
    try {
      const res = await api('post', '/api/admin/invoices/generate-batch');
      const nErr = res.data.errors?.length || 0;
      setMessage(`${res.data.created_count} facture(s) generee(s)${nErr ? ` — ${nErr} erreur(s)` : ''}.`);
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setAction({ id: null, kind: null });
    }
  };
  const rectify = async (invoiceId) => {
    setAction({ id: invoiceId, kind: 'rect' });
    setMessage('');
    try {
      const res = await api('post', `/api/admin/invoices/${invoiceId}/rectify`);
      setMessage(`Facture rectificative ${res.data.invoice_number} generee.`);
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setAction({ id: null, kind: null });
    }
  };
  const sendEmail = async (invoiceId) => {
    setAction({ id: invoiceId, kind: 'send' });
    setMessage('');
    try {
      const res = await api('post', `/api/admin/invoices/${invoiceId}/send`);
      setMessage(`Envoyee a ${res.data.recipient}.`);
      await fetch();
    } catch (e) {
      setMessage('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setAction({ id: null, kind: null });
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      {siretMissing && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" data-testid="invoices-siret-warning">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-semibold text-red-800">Mode Brouillon actif</p>
            <p className="text-xs text-red-700 mt-1">
              Renseignez votre SIRET dans <a href="/admin/parametres" className="underline">les parametres</a> pour pouvoir envoyer les factures par email. En attendant, elles portent le filigrane BROUILLON.
            </p>
          </div>
        </div>
      )}
      {message && <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200" data-testid="invoices-message">{message}</div>}

      {/* Eligible-to-invoice reservations */}
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="p-4 border-b border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-[#2C3E30]">Reservations directes non encore facturees ({eligible.length})</h3>
            <p className="text-xs text-[#595959] mt-1">Airbnb / Booking sont exclus (facturation deleguee a la plateforme).</p>
          </div>
          <div
            title={siretMissing ? 'Renseignez votre SIRET avant de generer les factures' : ''}
            data-testid="invoice-batch-tooltip-wrapper"
          >
            <Button
              data-testid="invoice-generate-batch-btn"
              onClick={generateBatch}
              disabled={siretMissing || eligible.length === 0 || action.kind === 'batch'}
              className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] text-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {action.kind === 'batch' ? 'Generation en cours...' : `Generer les ${eligible.length} facture${eligible.length > 1 ? 's' : ''} manquante${eligible.length > 1 ? 's' : ''} en masse`}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F5F0]">
                <TableHead className="text-xs">Client</TableHead>
                <TableHead className="text-xs">Dates</TableHead>
                <TableHead className="text-xs">Formule</TableHead>
                <TableHead className="text-xs text-right">Montant TTC</TableHead>
                <TableHead className="text-xs text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eligible.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#595959] text-sm">Toutes les reservations directes ont leur facture.</TableCell></TableRow>
              ) : eligible.map(r => (
                <TableRow key={r.id} data-testid={`invoice-eligible-${r.id}`} className="hover:bg-[#FAFAF9]">
                  <TableCell className="text-sm py-2">{r.name}</TableCell>
                  <TableCell className="text-xs py-2">{r.arrival_date} → {r.departure_date}</TableCell>
                  <TableCell className="text-xs py-2">{r.accommodation_type}</TableCell>
                  <TableCell className="text-right text-sm py-2 font-medium">{formatEuro(r.amount)}</TableCell>
                  <TableCell className="text-center py-2">
                    <Button
                      data-testid={`invoice-generate-btn-${r.id}`}
                      onClick={() => generate(r.id)}
                      disabled={action.id === r.id && action.kind === 'gen'}
                      size="sm"
                      className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] text-xs h-8"
                    >
                      {action.id === r.id && action.kind === 'gen' ? 'Generation...' : 'Generer la facture'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Existing invoices */}
      <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="p-4 border-b border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold text-[#2C3E30]">Historique des factures ({(() => {
            const yFiltered = yearFilter === 'all' ? invoices : invoices.filter(i => i.year === yearFilter);
            return yFiltered.length;
          })()})</h3>
          <Select value={String(yearFilter)} onValueChange={v => setYearFilter(v === 'all' ? 'all' : parseInt(v))}>
            <SelectTrigger data-testid="invoices-year-filter" className="w-32 rounded-full border-[#E5E5E0] h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F5F0]">
                <TableHead className="text-xs">Numero</TableHead>
                <TableHead className="text-xs">Client</TableHead>
                <TableHead className="text-xs">Emise le</TableHead>
                <TableHead className="text-xs text-right">Total TTC</TableHead>
                <TableHead className="text-xs">Statut</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-[#595959]">Chargement...</TableCell></TableRow>
              ) : (yearFilter === 'all' ? invoices : invoices.filter(i => i.year === yearFilter)).length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-[#595959] text-sm">Aucune facture pour {yearFilter === 'all' ? 'la periode' : yearFilter}.</TableCell></TableRow>
              ) : (yearFilter === 'all' ? invoices : invoices.filter(i => i.year === yearFilter)).map(i => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.id}`} className="hover:bg-[#FAFAF9]">
                  <TableCell className="text-sm py-2 font-mono">
                    {i.invoice_number}
                    {i.parent_invoice_number && <div className="text-[10px] text-[#8A8A85]">rectifie {i.parent_invoice_number}</div>}
                  </TableCell>
                  <TableCell className="text-sm py-2">{i.client_name}</TableCell>
                  <TableCell className="text-xs py-2">{(i.issued_at || '').slice(0, 10)}</TableCell>
                  <TableCell className="text-right text-sm py-2 font-medium">{formatEuro(i.total_ttc)}</TableCell>
                  <TableCell className="text-xs py-2">
                    {i.is_draft ? <Badge className="bg-red-100 text-red-800 border-red-200 border text-[10px]">BROUILLON</Badge>
                      : i.sent_at ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]">Envoyee</Badge>
                      : <Badge className="bg-sky-100 text-sky-800 border-sky-200 border text-[10px]">Finale</Badge>}
                    {i.is_rectificative && <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px] ml-1">Rectif.</Badge>}
                  </TableCell>
                  <TableCell className="text-right py-2">
                    <div className="flex justify-end gap-1.5">
                      <a
                        href={`${BACKEND_URL}/api/admin/invoices/${i.id}/pdf?token=${encodeURIComponent(token)}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`invoice-download-${i.id}`}
                        className="text-xs px-3 h-7 inline-flex items-center rounded-full bg-[#F5F5F0] hover:bg-[#E5E5E0] text-[#2C3E30]"
                      >PDF</a>
                      {!i.is_draft && !i.sent_at && (
                        <Button data-testid={`invoice-send-${i.id}`} onClick={() => sendEmail(i.id)} disabled={action.id === i.id} size="sm" variant="outline" className="rounded-full text-xs h-7 border-[#E5E5E0]">
                          {action.id === i.id && action.kind === 'send' ? '...' : 'Envoyer'}
                        </Button>
                      )}
                      {!i.is_rectificative && (
                        <Button data-testid={`invoice-rectify-${i.id}`} onClick={() => rectify(i.id)} disabled={action.id === i.id} size="sm" variant="ghost" className="rounded-full text-xs h-7">
                          {action.id === i.id && action.kind === 'rect' ? '...' : 'Rectifier'}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────
export default function AdminAccounting() {
  const { api, logout } = useAdminApi();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [yearly, setYearly] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ov, mo, yr] = await Promise.all([
        api('get', '/api/admin/accounting/overview'),
        api('get', '/api/admin/accounting/monthly'),
        api('get', '/api/admin/accounting/yearly'),
      ]);
      setOverview(ov.data);
      setMonthly(mo.data);
      setYearly(yr.data);
    } catch { logout(); }
  }, [api, logout]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const TABS = [
    { key: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
    { key: 'monthly', label: 'Par mois', icon: CalendarDays },
    { key: 'yearly', label: 'Par annee', icon: TrendingUp },
    { key: 'details', label: 'Detail', icon: FileText },
    { key: 'taxe', label: 'Taxe sejour', icon: Receipt },
    { key: 'expenses', label: 'Charges', icon: Coins },
    { key: 'vat', label: 'TVA nette', icon: PiggyBank },
    { key: 'obligations', label: 'Obligations', icon: ShieldAlert },
    { key: 'loan', label: 'Emprunt', icon: Landmark },
    { key: 'invoices', label: 'Factures', icon: FileDown },
    { key: 'entries', label: 'Ecritures', icon: PiggyBank },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="accounting-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Comptabilite</h1>
            <Badge className="bg-[#C4A962]/20 text-[#C4A962] border-none text-xs">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="accounting-logout-btn" onClick={logout} variant="ghost" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10" size="sm">
              <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Deconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map(t => (
            <Button
              key={t.key}
              data-testid={`accounting-tab-${t.key}`}
              variant={tab === t.key ? 'default' : 'outline'}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-5 text-sm ${
                tab === t.key ? 'bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]' : 'border-[#E5E5E0] text-[#595959]'
              }`}
            >
              <t.icon className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {t.label}
            </Button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && <OverviewTab overview={overview} api={api} />}
        {tab === 'monthly' && <MonthlyTab monthly={monthly} />}
        {tab === 'yearly' && <YearlyTab yearly={yearly} />}
        {tab === 'details' && <DetailsTab api={api} onRefresh={fetchAll} />}
        {tab === 'taxe' && <TaxeSejourTab api={api} />}
        {tab === 'expenses' && <ExpensesTab api={api} />}
        {tab === 'vat' && <VatTab api={api} />}
        {tab === 'obligations' && <ObligationsTab api={api} />}
        {tab === 'loan' && <LoanTab api={api} />}
        {tab === 'invoices' && <InvoicesTab api={api} />}
        {tab === 'entries' && <EntriesTab api={api} onRefresh={fetchAll} />}
      </div>
    </div>
  );
}
